import React, { useContext, useMemo } from "react"
import { Button, Col, Form, Row } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import { Formik } from "formik"
import _ from "lodash"

import {
  getFieldValues,
  getSectionFieldNames,
  getSettings,
  isOperatorTourIdea,
  templatePrimaryDocumentFieldRenders,
  templatePrimaryFieldRenders,
} from "@/onboarding/features/product/selectors"
import { updateFields } from "@/onboarding/features/product/Slice"
import FormikBootstrap from "@/components/formik-bootstrap"
import {
  CRUISE_LINE_FIELD_PLACEHOLDER,
  ReferencedValueTypes,
  ReservedFields,
} from "@/onboarding/features/product/models/Product"
import { DAY_OF_WEEK_OPTIONS } from "@/onboarding/features/product/models/Field"
import ReferencedValueSelect from "@/onboarding/features/product/components/shared/ReferencedValueSelect"
import SubmitButton from "@/onboarding/features/product/components/shared/buttons/SubmitButton"
import SubmitAndApplyButton from "@/onboarding/features/product/components/shared/buttons/SubmitAndApplyButton"
import SubmittingSpinner from "@/onboarding/features/product/components/shared/SubmittingSpinner"
import { SubmittingContext } from "@/onboarding/features/product/helpers"
import TrackFormDirty from "@/onboarding/features/product/forms/TrackFormDirty"

const Edit = ({ setEditing }): JSX.Element => {
  const dispatch = useDispatch()
  const fieldNames = useSelector(getSectionFieldNames.tourDetails)
  const initialValues = useSelector(getFieldValues(...fieldNames))
  const fieldsSettings = useSelector(getSettings(...fieldNames))
  const [submitting, setSubmitting] = useContext(SubmittingContext)
  const isTourOperatorIdea = useSelector(isOperatorTourIdea)
  const showTimeOfDay = useSelector(templatePrimaryDocumentFieldRenders("time_of_day"))
  const showEveningExcursion = useSelector(templatePrimaryFieldRenders("evening_excursion"))
  const cruiseLineFieldMessage = isTourOperatorIdea ? CRUISE_LINE_FIELD_PLACEHOLDER : ""

  // time_of_day was historically a single string; the schema is now a
  // text_list (array of strings). Coerce a legacy scalar value into a
  // single-element array so the MultiSelect can render. The next save
  // persists the canonical array form. Only when the active document has
  // the field: injecting the key otherwise makes every save submit a
  // field the update pipeline rejects (bid documents never carry it).
  const normalizedInitialValues = useMemo(() => {
    if (!(ReservedFields.TIME_OF_DAY in initialValues)) return initialValues
    const tod = initialValues[ReservedFields.TIME_OF_DAY]
    if (Array.isArray(tod)) return initialValues
    const coerced = tod === null || tod === undefined || tod === "" ? [] : [tod]
    return { ...initialValues, [ReservedFields.TIME_OF_DAY]: coerced }
  }, [initialValues])

  return (
    <Formik
      enableReinitialize
      initialValues={normalizedInitialValues}
      onSubmit={(values) => {
        setSubmitting(true)
        dispatch(updateFields({ values }))
      }}
    >
      {({ handleSubmit }) => (
        <Form className="d-flex flex-column gap-3" onSubmit={handleSubmit}>
          <TrackFormDirty formId="tourDetails" />
          <Row>
            <Col md={3}>
              <FormikBootstrap.NumberInput
                name={ReservedFields.TARGET_MINIMUM_DEPARTURE_PAX}
                label="Minimum Per Departure"
                readOnly={!fieldsSettings[ReservedFields.TARGET_MINIMUM_DEPARTURE_PAX].writeable}
                showLockedIcon={
                  !fieldsSettings[ReservedFields.TARGET_MINIMUM_DEPARTURE_PAX].writeable
                }
                showRequired={fieldsSettings[ReservedFields.TARGET_MINIMUM_DEPARTURE_PAX].required}
              />
            </Col>
            <Col md={3}>
              <FormikBootstrap.NumberInput
                name={ReservedFields.TARGET_MAXIMUM_DEPARTURE_PAX}
                label="Maximum Per Departure"
                readOnly={!fieldsSettings[ReservedFields.TARGET_MAXIMUM_DEPARTURE_PAX].writeable}
                showLockedIcon={
                  !fieldsSettings[ReservedFields.TARGET_MAXIMUM_DEPARTURE_PAX].writeable
                }
                showRequired={fieldsSettings[ReservedFields.TARGET_MAXIMUM_DEPARTURE_PAX].required}
              />
            </Col>
            <Col md={3}>
              <FormikBootstrap.NumberInput
                name={ReservedFields.TARGET_MAXIMUM_CALL_PAX}
                label="Maximum Per Call"
                readOnly={!fieldsSettings[ReservedFields.TARGET_MAXIMUM_CALL_PAX].writeable}
                showLockedIcon={!fieldsSettings[ReservedFields.TARGET_MAXIMUM_CALL_PAX].writeable}
                showRequired={fieldsSettings[ReservedFields.TARGET_MAXIMUM_CALL_PAX].required}
              />
            </Col>
            {showTimeOfDay && (
              <Col md={3}>
                <FormikBootstrap.MultiSelect
                  name={ReservedFields.TIME_OF_DAY}
                  label="Time of Day"
                  placeholder="Not specified"
                  options={[
                    { label: "Morning", value: "Morning" },
                    { label: "Afternoon", value: "Afternoon" },
                    { label: "Evening", value: "Evening" },
                    { label: "Late Evening", value: "Late Evening" },
                  ]}
                />
              </Col>
            )}
            <Col hidden>
              <FormikBootstrap.NumberInput
                name={ReservedFields.TARGET_DEPARTURES}
                label="Departures"
                readOnly={!fieldsSettings[ReservedFields.TARGET_DEPARTURES].writeable}
                showLockedIcon={!fieldsSettings[ReservedFields.TARGET_DEPARTURES].writeable}
                showRequired={fieldsSettings[ReservedFields.TARGET_DEPARTURES].required}
              />
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <ReferencedValueSelect
                name={ReservedFields.TOUR_TYPE}
                type={ReferencedValueTypes.TOUR_TYPE}
                label="Tour Types"
                isDisabled={!fieldsSettings[ReservedFields.TOUR_TYPE].writeable}
                showRequired={fieldsSettings[ReservedFields.TOUR_TYPE].required}
                emptyMessage={cruiseLineFieldMessage}
              />
            </Col>
            <Col md={3}>
              <ReferencedValueSelect
                name={ReservedFields.ACTIVITY_TYPE}
                type={ReferencedValueTypes.ACTIVITY_TYPE}
                label="Activity Type"
                isDisabled={!fieldsSettings[ReservedFields.ACTIVITY_TYPE].writeable}
                showRequired={fieldsSettings[ReservedFields.ACTIVITY_TYPE].required}
                emptyMessage={cruiseLineFieldMessage}
              />
            </Col>
            <Col md={3}>
              <ReferencedValueSelect
                name={ReservedFields.ACTIVITY_LEVEL}
                type={ReferencedValueTypes.ACTIVITY_LEVEL}
                label="Activity Level"
                isDisabled={!fieldsSettings[ReservedFields.ACTIVITY_LEVEL].writeable}
                showRequired={fieldsSettings[ReservedFields.ACTIVITY_LEVEL].required}
                emptyMessage={cruiseLineFieldMessage}
              />
            </Col>
          </Row>
          <FormikBootstrap.TextAreaInput
            name={ReservedFields.SHORT_TOUR_DESCRIPTION}
            label="Short Tour Description"
            readOnly={!fieldsSettings[ReservedFields.SHORT_TOUR_DESCRIPTION].writeable}
            showLockedIcon={!fieldsSettings[ReservedFields.SHORT_TOUR_DESCRIPTION].writeable}
            showRequired={fieldsSettings[ReservedFields.SHORT_TOUR_DESCRIPTION].required}
            helpText="Less Than 1000 Characters"
          />
          <FormikBootstrap.TextAreaInput
            rows={5}
            name={ReservedFields.TOUR_DESCRIPTION}
            label="Full Tour Description"
            readOnly={!fieldsSettings[ReservedFields.TOUR_DESCRIPTION].writeable}
            showLockedIcon={!fieldsSettings[ReservedFields.TOUR_DESCRIPTION].writeable}
            showRequired={fieldsSettings[ReservedFields.TOUR_DESCRIPTION].required}
          />
          <FormikBootstrap.TextAreaInput
            name={ReservedFields.TOUR_NOTES}
            label="Tour Notes"
            readOnly={!fieldsSettings[ReservedFields.TOUR_NOTES].writeable}
            showLockedIcon={!fieldsSettings[ReservedFields.TOUR_NOTES].writeable}
            showRequired={fieldsSettings[ReservedFields.TOUR_NOTES].required}
          />
          <FormikBootstrap.TextInput
            name={ReservedFields.TOP_THREE_REASONS}
            label="Top Three Reasons A Guest Should Buy This Tour"
            readOnly={!fieldsSettings[ReservedFields.TOP_THREE_REASONS].writeable}
            showLockedIcon={!fieldsSettings[ReservedFields.TOP_THREE_REASONS].writeable}
            showRequired={fieldsSettings[ReservedFields.TOP_THREE_REASONS].required}
          />
          <FormikBootstrap.TextInput
            name={ReservedFields.CANCELLATION_POLICY_DESCRIPTION}
            label="Cancellation Policy"
            readOnly={!fieldsSettings[ReservedFields.CANCELLATION_POLICY_DESCRIPTION].writeable}
            showLockedIcon={
              !fieldsSettings[ReservedFields.CANCELLATION_POLICY_DESCRIPTION].writeable
            }
            showRequired={fieldsSettings[ReservedFields.CANCELLATION_POLICY_DESCRIPTION].required}
          />
          <Row>
            <Col md={6}>
              <FormikBootstrap.MultiSelect
                name={ReservedFields.UNAVAILABLE_DAYS}
                label="Days On Which The Tour Will Be Unavailable"
                options={DAY_OF_WEEK_OPTIONS}
                showLockedIcon={!fieldsSettings[ReservedFields.UNAVAILABLE_DAYS].writeable}
                disabled={!fieldsSettings[ReservedFields.UNAVAILABLE_DAYS].writeable}
              />
            </Col>
            <Col md={6}>
              <FormikBootstrap.TextInput
                name={ReservedFields.OTHER_RELEVANT_UNAVAILABLE_INFORMATION}
                label="Other Relevant Information On Days And Times The Tour Is Unavailable"
                readOnly={
                  !fieldsSettings[ReservedFields.OTHER_RELEVANT_UNAVAILABLE_INFORMATION].writeable
                }
                showLockedIcon={
                  !fieldsSettings[ReservedFields.OTHER_RELEVANT_UNAVAILABLE_INFORMATION].writeable
                }
              />
            </Col>
          </Row>
          <Row>
            {_.includes(fieldNames, ReservedFields.GROUND_OPERATOR) && (
              <Col md={6}>
                <FormikBootstrap.TextInput
                  name={ReservedFields.GROUND_OPERATOR}
                  label="Ground Operator"
                  readOnly={!fieldsSettings[ReservedFields.GROUND_OPERATOR].writeable}
                  showLockedIcon={!fieldsSettings[ReservedFields.GROUND_OPERATOR].writeable}
                />
              </Col>
            )}
            {showEveningExcursion && (
              <Col md={3}>
                <div className="form-label">&nbsp;</div>
                <div className="d-flex align-items-center">
                  <FormikBootstrap.Switch
                    name={ReservedFields.EVENING_EXCURSION}
                    label="Evening Excursion"
                    disabled={!fieldsSettings[ReservedFields.EVENING_EXCURSION].writeable}
                    showLockedIcon={!fieldsSettings[ReservedFields.EVENING_EXCURSION].writeable}
                  />
                </div>
              </Col>
            )}
            <Col md={3}>
              <div className="form-label">&nbsp;</div>
              <div className="d-flex align-items-center">
                <FormikBootstrap.Switch
                  name={ReservedFields.ADA_COMPLIANT}
                  label="ADA Compliant"
                  disabled={!fieldsSettings[ReservedFields.ADA_COMPLIANT].writeable}
                  showLockedIcon={!fieldsSettings[ReservedFields.ADA_COMPLIANT].writeable}
                />
              </div>
            </Col>
          </Row>
          <div className="d-flex gap-2">
            <SubmitButton />
            <SubmitAndApplyButton />
            <Button
              size="sm"
              variant="outline-danger"
              onClick={() => setEditing(false)}
              disabled={submitting}
            >
              <i className="fas fa-times me-2" />
              Cancel
            </Button>
            <SubmittingSpinner />
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default Edit
